import { Link, createFileRoute } from '@tanstack/react-router'

import { useGetTournament } from '../api/generated/endpoints/tournaments/tournaments'
import { formatUYU } from '../lib/money'
import { formatDayLong } from '../lib/time'
import {
  TOURNAMENT_STATUS_LABELS,
  handicapLabel,
  positionLabel,
  whenLabel,
} from '../lib/tournaments'

/**
 * The public scoreboard, and the reason this whole feature spreads.
 *
 * NO AUTHENTICATION, deliberately. This URL exists to be pasted into a WhatsApp
 * group where most readers have never signed in and some have never played.
 * Putting a login in front of a scoreboard would quietly kill the one part of
 * the system that markets itself.
 *
 * It is also a route OUTSIDE `_authed`, at a short path — `/t/agosto-2026`
 * survives being retyped from a screenshot.
 */
export const Route = createFileRoute('/t/$slug')({
  component: StandingsPage,
})

function StandingsPage() {
  const { slug } = Route.useParams()
  const tournament = useGetTournament(slug)

  if (tournament.isPending) return <p className="text-slate-600">Cargando…</p>

  if (tournament.isError) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">No encontramos ese torneo.</h1>
        <p className="text-slate-600">
          Puede que el enlace esté mal, o que el torneo todavía no se haya publicado.
        </p>
        <Link to="/tournaments" className="text-slate-900 underline">
          Ver los próximos torneos
        </Link>
      </div>
    )
  }

  const event = tournament.data
  const standings = event.standings
  const played = standings.filter((row) => row.position !== null)

  return (
    <div className="space-y-6">
      <header>
        <p className="text-sm font-medium uppercase tracking-wide text-slate-500">
          {TOURNAMENT_STATUS_LABELS[event.status] ?? event.status}
        </p>
        <h1 className="mt-1 text-2xl font-semibold">{event.name}</h1>
        <p className="mt-1 text-slate-600">
          {whenLabel(event.event_date, event.plays_from, event.plays_until, formatDayLong)}
          {event.gspro_course && <> &middot; {event.gspro_course}</>}
        </p>
      </header>

      {event.notes && (
        <p className="whitespace-pre-line rounded-lg border border-slate-200 bg-white p-4 text-slate-700">
          {event.notes}
        </p>
      )}

      {standings.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-slate-600">
          Todavía no hay inscriptos.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          {/* overflow-x-AUTO on the wrapper, not hidden. It was
              `overflow-hidden` to clip the corners against the rounded border,
              which meant a table wider than the viewport lost its right-hand
              columns SILENTLY -- no scrollbar, no hint, the Neto column simply
              gone. It fitted in English at 340px; the Spanish headers are
              longer.

              A table, not a list of cards: a scoreboard is columns of numbers
              that people compare down the page, and cards destroy that. */}
          <table className="w-full min-w-[20rem] text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th scope="col" className="w-12 px-3 py-2 text-right">
                  Pos
                </th>
                <th scope="col" className="px-3 py-2">
                  Jugador
                </th>
                <th scope="col" className="w-14 px-3 py-2 text-right">
                  Bruto
                </th>
                <th scope="col" className="w-14 px-3 py-2 text-right">
                  Hcp
                </th>
                <th scope="col" className="w-14 px-3 py-2 text-right">
                  Neto
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {standings.map((row, index) => (
                <tr
                  key={`${row.player}-${index}`}
                  className={row.position === null ? 'text-slate-400' : undefined}
                >
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {positionLabel(standings, index)}
                  </td>
                  <td className="px-3 py-2">{row.player}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.gross_strokes ?? '–'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {handicapLabel(row.handicap)}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">
                    {row.net ?? '–'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-sm text-slate-500">
        {/* Nominal on purpose. A verb here has to agree with the FIRST count
            ("1 de 2 ... anotó") while the noun agrees with the second, and
            getting that right in a template is more trouble than the sentence
            is worth. */}
        Tarjetas anotadas: {played.length} de {event.entrants}. Clasificación por golpes netos.
      </p>

      {event.status === 'open' && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-slate-700">
            Inscripciones abiertas
            {event.entry_fee_uyu_pesos > 0 && <> &mdash; {formatUYU(event.entry_fee_uyu_pesos)}</>}.
          </p>
          <Link
            to="/tournaments"
            className="mt-3 inline-flex min-h-12 items-center rounded-md bg-slate-900 px-5 text-white hover:bg-slate-700"
          >
            Inscribirse en este torneo
          </Link>
        </div>
      )}
    </div>
  )
}
